import { useState } from 'react';
import { useToast } from '@/shared/hooks/use-toast';
import { apiClient } from '@/shared/services/api';
import { API_ROUTES } from '@/shared/services/apiRoutes';

export type CallOutcome = 'deal_closed' | 'bid_sent' | 'not_interested' | 'no_outcome';

interface CallReportData {
  roomId: string;
  durationMinutes: number;
  outcome: CallOutcome;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
}

export const useCallReports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const saveCallReport = async (data: CallReportData) => {
    setIsLoading(true);
    try {
      // ✅ NEW: Use backend API instead of direct Supabase call
      await apiClient.post(API_ROUTES.CALL_REPORTS_CREATE, {
        room_id: data.roomId,
        call_duration: data.durationMinutes,
        customer_name: data.customerName,
        customer_email: data.customerEmail,
        customer_phone: data.customerPhone,
        notes: data.notes,
        // Note: outcome field not in backend schema yet, add if needed
      });

      toast({
        title: "Call Report Saved",
        description: "Call data has been saved to your reports.",
      });

      return true;
    } catch (error) {
      console.error('Error saving call report:', error);
      toast({
        title: "Error",
        description: "Failed to save call report. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const getCallReports = async () => {
    try {
      // ✅ NEW: Use backend API instead of direct Supabase call
      const response = await apiClient.get<{ ok: boolean; data: any[] }>(API_ROUTES.CALL_REPORTS_LIST);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching call reports:', error);
      return [];
    }
  };

  const getCallStats = async () => {
    try {
      // ✅ NEW: Use backend API instead of direct Supabase call
      const response = await apiClient.get<{ ok: boolean; data: any[] }>(API_ROUTES.CALL_REPORTS_LIST);
      const data = response.data || [];

      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

      const todaysCalls = data.filter(call =>
        new Date(call.call_date || call.created_at) >= todayStart
      );

      const totalMinutes = data.reduce((sum, call) => sum + (call.call_duration || call.duration_minutes || 0), 0);
      const dealsCount = data.filter(call => call.outcome === 'deal_closed').length;
      const bidsCount = data.filter(call => call.outcome === 'bid_sent').length;

      return {
        totalCalls: data.length,
        callsToday: todaysCalls.length,
        totalMinutes,
        averageDuration: data.length ? Math.round(totalMinutes / data.length) : 0,
        dealsCount,
        bidsCount,
        conversionRate: data.length ? Math.round((dealsCount / data.length) * 100) : 0,
      };
    } catch (error) {
      console.error('Error fetching call stats:', error);
      return {
        totalCalls: 0,
        callsToday: 0,
        totalMinutes: 0,
        averageDuration: 0,
        dealsCount: 0,
        bidsCount: 0,
        conversionRate: 0,
      };
    }
  };

  return {
    saveCallReport,
    getCallReports,
    getCallStats,
    isLoading,
  };
};